//SCR-052 (S2.4.7): bảng phân quyền của một tài khoản. Cột chỉ dùng nhãn nghiệp vụ —
//role/action code, UUID, version nằm ngoài DOM theo IRM; version theo sát từng dòng
//chỉ đi ngầm khi thu hồi. "Dải hiệu lực" tái dùng điểm nhận diện của SCR-050.
import { Button, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import type { AdminVersion } from "../administration/adminTypes";
import { ValidityBand } from "../administration/components/ValidityBand";
import {
  ASSIGNMENT_BADGE_STATUS,
  ASSIGNMENT_STATE_LABEL,
  deriveAssignmentState,
  formatAssignmentDate,
  scopeSummary,
} from "./roleAssignmentAdminApi";
import type {
  CatalogRoleOption,
  RoleAssignmentItem,
} from "./roleAssignmentAdminApi";

export function RoleAssignmentTable({
  items,
  catalogRoles,
  orgNames,
  areaNames,
  canRevoke,
  onRevoke,
}: {
  readonly items: readonly RoleAssignmentItem[];
  readonly catalogRoles: readonly CatalogRoleOption[];
  readonly orgNames: ReadonlyMap<string, string>;
  readonly areaNames: ReadonlyMap<string, string>;
  readonly canRevoke: boolean;
  readonly onRevoke: (item: RoleAssignmentItem) => void;
}) {
  const roleLabel = useMemo(() => {
    const byCode = new Map(catalogRoles.map((role) => [role.code, role.displayName]));
    return (code: string) => byCode.get(code) ?? "Vai trò không còn trong danh mục";
  }, [catalogRoles]);

  const bandVersions = useMemo<AdminVersion[]>(() => items.map((item, index) => {
    const state = deriveAssignmentState(item);
    return {
      version: index,
      name: roleLabel(item.roleCode),
      classification: "",
      status: ASSIGNMENT_STATE_LABEL[state],
      validFrom: item.validFrom.slice(0, 10),
      validTo: item.validTo ? item.validTo.slice(0, 10) : null,
    };
  }), [items, roleLabel]);
  const currentSegment = items.map((item) => deriveAssignmentState(item))
    .lastIndexOf("EFFECTIVE");

  const columns = useMemo<ColumnsType<RoleAssignmentItem>>(() => [
    {
      title: "Vai trò",
      dataIndex: "roleCode",
      render: (_value, item) => roleLabel(item.roleCode),
    },
    {
      title: "Phạm vi",
      dataIndex: "assignmentId",
      render: (_value, item) => scopeSummary(item, orgNames, areaNames),
    },
    {
      title: "Thời hiệu",
      dataIndex: "validFrom",
      render: (_value, item) => (
        <span>
          {formatAssignmentDate(item.validFrom)}
          {" → "}
          {item.validTo ? formatAssignmentDate(item.validTo) : "vô thời hạn"}
        </span>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "validFrom",
      key: "state",
      render: (_value, item) => {
        const state = deriveAssignmentState(item);
        return <Tag color={ASSIGNMENT_BADGE_STATUS[state]}>{ASSIGNMENT_STATE_LABEL[state]}</Tag>;
      },
    },
    {
      title: "Hành động",
      dataIndex: "assignmentId",
      key: "actions",
      render: (_value, item) => {
        const state = deriveAssignmentState(item);
        const revocable = state === "EFFECTIVE" || state === "SCHEDULED";
        if (!canRevoke || !revocable) {
          return null;
        }
        return (
          <Button danger size="small" onClick={() => onRevoke(item)}>
            Thu hồi
          </Button>
        );
      },
    },
  ], [areaNames, canRevoke, onRevoke, orgNames, roleLabel]);

  return (
    <div className="role-assignments-table">
      {bandVersions.length > 0 && (
        <ValidityBand versions={bandVersions} currentVersion={currentSegment} />
      )}
      <Table<RoleAssignmentItem>
        rowKey="assignmentId"
        size="small"
        aria-label="Danh sách vai trò được phân công"
        columns={columns}
        dataSource={[...items]}
        pagination={false}
        onRow={(item) => ({ "aria-label": roleLabel(item.roleCode) })}
      />
    </div>
  );
}
